import { Lock, Clock, Wrench, Database, ShieldCheck, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  AgentMemoryResponse,
  AgentSkillsResponse,
  AgentSummary,
} from '@/lib/api'

// ─── Shared bits ──────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'JUST NOW'
  if (mins < 60) return `${mins}M AGO`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}H AGO`
  return `${Math.floor(hours / 24)}D AGO`
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function SectionLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
      {icon}
      {children}
    </p>
  )
}

function TabError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 bg-cmd-error-container p-3">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cmd-on-error" aria-hidden="true" />
      <p className="font-label text-xs text-cmd-on-error">{message}</p>
    </div>
  )
}

function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  )
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-cmd-low p-6 text-center">
      <p className="font-label text-xs text-cmd-outline">{children}</p>
    </div>
  )
}

// ─── Memory tab ────────────────────────────────────────────────────────────────

export function MemoryTab({
  data,
  isLoading,
  error,
}: {
  data: AgentMemoryResponse | undefined
  isLoading: boolean
  error: Error | null
}) {
  if (isLoading) return <ListSkeleton rows={4} />
  if (error) return <TabError message={`Could not load memory — ${error.message}`} />
  if (!data || data.keys.length === 0) {
    return <EmptyState>No memory keys stored yet for this agent.</EmptyState>
  }

  return (
    <div className="space-y-3">
      <p className="font-mono text-[0.65rem] text-cmd-outline tabular-nums">
        {data.count} KEY{data.count === 1 ? '' : 'S'} · NAMESPACE {data.skillName}
      </p>
      <ul className="space-y-px">
        {data.keys.map((entry) => (
          <li key={`${entry.namespace}/${entry.key}`} className="bg-cmd-low p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-xs text-white break-all">
                  <span className="text-cmd-outline">{entry.namespace}/</span>
                  {entry.key}
                </p>
                <p className="mt-0.5 font-mono text-[0.6rem] text-cmd-outline-variant tabular-nums">
                  v{entry.version} · UPDATED {timeAgo(entry.updated_at)}
                </p>
              </div>
              {entry.sensitive && (
                <Badge variant="warning" className="shrink-0 gap-1 text-[9px] px-1.5 py-0 font-mono">
                  <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                  SENSITIVE
                </Badge>
              )}
            </div>
            <div className="mt-2">
              {entry.redacted ? (
                <p className="font-mono text-[0.7rem] text-cmd-outline-variant">[REDACTED]</p>
              ) : (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words bg-cmd-base p-2 font-mono text-[0.7rem] leading-relaxed text-white/85">
                  {formatValue(entry.value)}
                </pre>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="font-label text-[0.6rem] text-cmd-outline-variant leading-relaxed">
        Read-only. Sensitive values are encrypted at rest and never leave the runtime.
      </p>
    </div>
  )
}

// ─── Skills tab ────────────────────────────────────────────────────────────────

export function SkillsTab({
  data,
  isLoading,
  error,
}: {
  data: AgentSkillsResponse | undefined
  isLoading: boolean
  error: Error | null
}) {
  if (isLoading) return <ListSkeleton rows={4} />
  if (error) return <TabError message={`Could not load skills — ${error.message}`} />
  if (!data) return <EmptyState>No skill configuration available.</EmptyState>

  return (
    <div className="space-y-4">
      {data.description && (
        <p className="font-label text-xs text-white/80 leading-relaxed">{data.description}</p>
      )}

      <div>
        <SectionLabel icon={<Wrench className="h-3 w-3" aria-hidden="true" />}>
          Tools · {data.tools.length}
        </SectionLabel>
        {data.tools.length === 0 ? (
          <p className="mt-1.5 font-mono text-[0.7rem] text-cmd-outline-variant">
            {data.loaded ? 'No tools declared.' : 'Skill not loaded in runtime — tools unavailable.'}
          </p>
        ) : (
          <ul className="mt-2 space-y-px">
            {data.tools.map((tool) => (
              <li key={tool.name} className="bg-cmd-low p-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-mono text-xs text-white break-all">{tool.name}</p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {tool.shared && (
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0 font-mono">
                        SHARED
                      </Badge>
                    )}
                    <Badge
                      variant={tool.implemented ? 'success' : 'outline'}
                      className="text-[9px] px-1.5 py-0 font-mono"
                    >
                      {tool.implemented ? 'LIVE' : 'STUB'}
                    </Badge>
                  </div>
                </div>
                <p className="mt-1 font-label text-[0.7rem] text-cmd-outline leading-snug">
                  {tool.description}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {data.memory && (
        <div className="grid gap-3 sm:grid-cols-2">
          <NamespaceList label="Reads from" items={data.memory.read_namespaces} />
          <NamespaceList label="Writes to" items={data.memory.write_namespaces} />
        </div>
      )}

      {data.decision && (
        <div>
          <SectionLabel icon={<ShieldCheck className="h-3 w-3" aria-hidden="true" />}>
            Decision authority
          </SectionLabel>
          <div className="mt-2 space-y-2 bg-cmd-low p-3">
            <KV
              label="Max autonomous actions"
              value={String(data.decision.max_autonomous_actions)}
            />
            <KV label="Safe default" value={data.decision.safe_default} />
            <KV label="Timeout" value={`${data.decision.timeout_minutes} min`} />
            {data.decision.requires_approval_for.length > 0 && (
              <div>
                <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">
                  Requires Mark&apos;s approval for
                </p>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {data.decision.requires_approval_for.map((item) => (
                    <li key={item}>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-mono">
                        {item}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function NamespaceList({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <SectionLabel icon={<Database className="h-3 w-3" aria-hidden="true" />}>{label}</SectionLabel>
      {items.length === 0 ? (
        <p className="mt-1.5 font-mono text-[0.7rem] text-cmd-outline-variant">—</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {items.map((ns) => (
            <li key={ns} className="font-mono text-[0.7rem] text-white/80 break-all">
              {ns}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Config tab ────────────────────────────────────────────────────────────────

export function ConfigTab({
  skills,
  live,
  isLoading,
  error,
}: {
  skills: AgentSkillsResponse | undefined
  live: AgentSummary | undefined
  isLoading: boolean
  error: Error | null
}) {
  if (isLoading) return <ListSkeleton rows={3} />
  if (error) return <TabError message={`Could not load config — ${error.message}`} />

  const lastRun = live?.last_run_at ?? null
  const outcome = live?.last_run_outcome ?? null

  return (
    <div className="space-y-4">
      <div>
        <SectionLabel icon={<Clock className="h-3 w-3" aria-hidden="true" />}>Schedule</SectionLabel>
        {!skills || skills.schedule.length === 0 ? (
          <p className="mt-1.5 font-mono text-[0.7rem] text-cmd-outline-variant">
            Event-driven · no cron schedule
          </p>
        ) : (
          <ul className="mt-2 space-y-px">
            {skills.schedule.map((s) => (
              <li key={s.cron} className="bg-cmd-low p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-sans text-xs font-medium text-white">{s.humanized}</p>
                  <span className="font-mono text-[0.6rem] text-cmd-outline">{s.timezone}</span>
                </div>
                <p className="mt-0.5 font-label text-[0.65rem] text-cmd-outline">{s.description}</p>
                <p className="mt-1 font-mono text-[0.6rem] text-cmd-outline-variant">{s.cron}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <KV label="Model" value={skills?.model ?? live?.model ?? '—'} mono />
        <KV
          label="Status"
          value={(skills?.status ?? live?.status ?? 'unknown').toUpperCase()}
          mono
        />
        <KV
          label="Last activation"
          value={`${timeAgo(lastRun)}${outcome ? ` · ${outcome.toUpperCase()}` : ''}`}
          mono
        />
        <KV
          label="Success 24h / 7d"
          value={`${live?.success_rate_24h != null ? `${live.success_rate_24h}%` : '--'} / ${
            live?.success_rate_7d != null ? `${live.success_rate_7d}%` : '--'
          }`}
          mono
        />
        <KV label="Runs today / 7d" value={`${live?.runs_today ?? 0} / ${live?.runs_7d ?? 0}`} mono />
        <KV
          label="Version"
          value={skills?.version ? `v${skills.version}` : '—'}
          mono
        />
      </div>
    </div>
  )
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase shrink-0">
        {label}
      </span>
      <span
        className={`text-right text-xs text-white/90 ${mono ? 'font-mono tabular-nums' : 'font-label'}`}
      >
        {value}
      </span>
    </div>
  )
}
